// Module 3 — Well Profile Hub.
//
// Lists all wells with a quick summary (name, operator, field, env, status,
// updated) and lets the user create / open / delete a well. This page is the
// entry point to the master record of RigSentinel.

import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, ArrowLeft, Trash2, Briefcase, Waves, Mountain } from "lucide-react";
import { listWellProfiles, deleteWellProfile } from "@/lib/well/storage";
import { useStackMode } from "@/lib/stackModeContext";
import { stackAccentClasses, stackAccentColor } from "@/lib/stackAccent";

// A well belongs to Subsea mode only when it's genuinely offshore with a
// subsea BOP; everything else (onshore, or offshore with a surface BOP) is
// a Surface-mode well. Mirrors the same rule WellProfileEditor already uses
// to coerce environment/bopLocation on Save.
// Well Profile Hub — normal entry to the master record. When arriving with
// ?intent=kill-sheets (Home's Kill Sheets card fallback, no active well),
// selecting a well resumes that intent instead of opening the well itself.
function _wellStackMode(w) {
  return (w?.environment === "offshore" && w?.bopLocation === "subsea") ? "subsea" : "surface";
}

const STATUS_COLOR = {
  planned:   "border-sky-500/40 text-sky-300",
  drilling:  "border-amber-500/50 text-amber-400",
  suspended: "border-yellow-500/40 text-yellow-300",
  completed: "border-emerald-500/40 text-emerald-300",
  abandoned: "border-zinc-500/40 text-zinc-400",
};

function StatusPill({ status }) {
  const cls = STATUS_COLOR[status] || "border-zinc-500/40 text-zinc-400";
  return (
    <span data-testid={`well-status-${status}`}
          className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[9.5px] uppercase tracking-[0.16em] ${cls}`}>
      {status}
    </span>
  );
}

function EnvIcon({ env }) {
  if (env === "offshore")
    return <Waves className="w-3.5 h-3.5 text-sky-400" aria-hidden="true" />;
  return <Mountain className="w-3.5 h-3.5 text-emerald-400" aria-hidden="true" />;
}

export default function WellProfileHub() {
  const navigate = useNavigate();
  // P0 (Phase 1 fallback correction) — carries the "open a Kill Sheet"
  // intent through the /wells fallback so picking an existing well here
  // resumes the canonical System B Kill Sheet workflow instead of just
  // opening the well. Read once; normal Well Profiles navigation (no
  // intent param) is unaffected.
  const killSheetIntent =
    new URLSearchParams(window.location.search).get("intent") === "kill-sheets";
  const { stackMode } = useStackMode();
  const accent = stackAccentClasses(stackMode);
  const [rows, setRows] = useState(() => listWellProfiles());
  const [q, setQ]       = useState("");

  const refresh = () => setRows(listWellProfiles());

  useEffect(() => {
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  // Surface/Subsea isolation — this hub is the entry point "Next → Well
  // Profile" lands on, so it must only ever list wells that belong to the
  // current operating mode. Existing wells from the other mode remain
  // safely stored, just not shown here.
  const modeRows = rows.filter((r) => _wellStackMode(r) === stackMode);

  const handleDelete = (id, name) => {
    if (!window.confirm(`Delete well "${name}"?\n\nThis removes the well record only. Reference libraries are untouched.`))
      return;
    deleteWellProfile(id);
    refresh();
  };

  const filtered = q.trim()
    ? modeRows.filter((r) => {
        const hay = [
          r.wellName, r.uwi, r.operator, r.client, r.field, r.block,
          r.country, r.rig, r.status,
        ].filter(Boolean).join(" ").toLowerCase();
        return hay.includes(q.toLowerCase());
      })
    : modeRows;

  return (
    <div data-testid="well-hub" className="flex flex-col gap-4 pb-8">
      <header className="flex items-center justify-between gap-3">
        <button type="button" data-testid="well-hub-back"
                onClick={() => navigate("/home")}
                className="inline-flex items-center gap-1.5 text-[12px] text-[var(--rs-text-2)] hover:text-[var(--rs-amber)]">
          <ArrowLeft className="w-3.5 h-3.5" /> Home
        </button>
        <Link
          to="/wells/new"
          data-testid="well-hub-new"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium"
          style={{ background: stackAccentColor(stackMode), color: "#0A0B0E" }}>
          <Plus className="w-3.5 h-3.5" /> New Well
        </Link>
      </header>

      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--rs-text-dim,#9aa6b2)]">
          Module 3 · Master record
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold mt-0.5 text-[var(--rs-text,#e8edf2)]">
          Well Profiles
        </h1>
        <p className="text-xs text-[var(--rs-text-dim,#9aa6b2)] mt-1 leading-relaxed">
          The master record for each well. BHAs, drill strings, mud programs,
          cement programs, kill sheets and daily reports will hang from here.
          Reference libraries are linked by ID — no duplicate data.
        </p>
      </div>

      <input
        type="text"
        placeholder="Filter wells — name, operator, field, status…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        data-testid="well-hub-search"
        className="input-rs"
      />

      {filtered.length === 0 ? (
        <div data-testid="well-hub-empty"
             className="card-rs p-6 text-center text-sm text-[var(--rs-text-dim,#9aa6b2)]">
          {modeRows.length === 0
            ? (rows.length === 0
                ? <>No wells yet. Tap <span className={`font-medium ${accent.text}`}>+ New Well</span> to define your first master record.</>
                : <>No {stackMode === "subsea" ? "Subsea" : "Surface"} wells yet in this mode. Existing {stackMode === "subsea" ? "Surface" : "Subsea"} wells are safely stored — switch modes to see them.</>)
            : <>No wells match the current filter.</>}
        </div>
      ) : (
        <div data-testid="well-hub-list" className="flex flex-col gap-2">
          {filtered.map((w) => (
            <div key={w.id}
                 data-testid={`well-row-${w.id}`}
                 className="card-rs p-3 flex items-start gap-3">
              <Link
                to={killSheetIntent ? `/wells/${w.id}/kill-sheets/new` : `/wells/${w.id}`}
                data-testid={`well-open-${w.id}`}
                className="flex-1 min-w-0"
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <Briefcase className={`w-3.5 h-3.5 shrink-0 ${accent.text}`} aria-hidden="true" />
                  <span className="font-semibold text-sm text-[var(--rs-text,#e8edf2)] truncate">
                    {w.wellName || "(unnamed)"}
                  </span>
                  <StatusPill status={w.status} />
                  <EnvIcon env={w.environment} />
                </div>
                <div className="mt-1 text-[11px] text-[var(--rs-text-dim,#9aa6b2)] flex items-center gap-2 flex-wrap">
                  {w.operator && <span>{w.operator}</span>}
                  {w.field    && <span>· {w.field}</span>}
                  {w.country  && <span>· {w.country}</span>}
                  {w.uwi      && <span>· UWI {w.uwi}</span>}
                </div>
                <div className="mt-1 text-[10px] uppercase tracking-[0.08em] text-[var(--rs-text-3,#6b7785)]">
                  {(w.children?.casingProgram?.length || 0)} casing string{w.children?.casingProgram?.length === 1 ? "" : "s"}
                  {" · "}
                  Updated {w.updatedAt ? new Date(w.updatedAt).toLocaleDateString() : "—"}
                </div>
              </Link>
              <button
                type="button"
                onClick={() => handleDelete(w.id, w.wellName)}
                data-testid={`well-delete-${w.id}`}
                aria-label={`Delete ${w.wellName}`}
                className="shrink-0 p-1.5 rounded-md text-zinc-400 hover:text-red-400 hover:bg-red-500/10"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
